'use client';
import { Fragment } from 'react';
import cn from 'classnames';
import Script from 'next/script';
import { useRouter } from 'next/navigation';

import TextLink from '@/components/common/Link/Link';
import Text from '@/components/common/Text/Text';
import { ProductBreadcrumb } from '@/types/Product';

import './Breadcrumbs.style.scss';

type BreadcrumbsProps = {
  className?: string;
  data: ProductBreadcrumb[];
};

function Breadcrumbs(props: BreadcrumbsProps) {
  const { className = '', data } = props;

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: data.map((breadcrumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@id': breadcrumb.url,
        name: breadcrumb.displayText
      }
    }))
  };

  return (
    <>
      <Script
        id='breadcrumb-schema'
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <div
        className={cn('Breadcrumbs', className)}
        aria-label='breadcrumb'
        data-testid='breadcrumbs'
      >
        {data.map((breadcrumb, index) => {
          const isLastItem = data.length - 1 === index;
          return (
            <Fragment key={index}>
              <TextLink
                className='Breadcrumbs-link'
                href={breadcrumb.url}
                compact
                aria-current={isLastItem ? 'page' : undefined}
                data-testid={
                  isLastItem ? 'breadcrumb-current' : 'breadcrumb-link'
                }
              >
                {breadcrumb.displayText}
              </TextLink>
              {!isLastItem && (
                <Text
                  className='Breadcrumbs-divider'
                  aria-hidden='true'
                >
                  &gt;
                </Text>
              )}
            </Fragment>
          );
        })}
      </div>
    </>
  );
}

export default Breadcrumbs;
